import React, { useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';

import { Portal } from '../portal/Portal';
import { getLeftPosition, getTopPosition, getTargetNode } from './utils';

import './WpPopover.scss';

type RefType = {
  current: HTMLElement;
};

type Size = 'sm' | 'md' | 'lg';
const DEFAULT_SIZE = 'md';

export type PlacementType = 'bottom' | 'bottom-start' | 'bottom-end' | 'right';
const DEFAULT_PLACEMENT = 'bottom';

export type Trigger = 'hover' | 'click';
const DEFAULT_TRIGGER = 'hover';

export interface Props {
  /**
   * Placement of popover. Options: 'bottom' | 'bottom-start' | 'bottom-end' | 'right'
   * @default 'bottom'
   */
  placement?: PlacementType;
  /**
   * Size of popover. Options: 'sm' | 'md' | 'lg'
   * @default 'md'
   */
  size?: Size;
  children?: React.ReactNode;
  target: RefType | string;
  indicatesTarget?: RefType | string;
  trigger?: Trigger;
  openOnMount?: boolean;
  closeOnBackdrop?: boolean;
  hideDelay?: number;
  onShow?(): void;
}

export interface ComponentProps {
  children?: React.ReactNode;
}

const classNamePrefix = 'WpPopover';

export function WpPopoverHeader({ centered = false, children }: ComponentProps) {
  const className = classnames(`${classNamePrefix}__header`, centered ? `${classNamePrefix}__header--centered` : false);
  return <div className={`${className}`}>{children}</div>;
}

export function WpPopoverBody({ children }: ComponentProps) {
  return <div className={`${classNamePrefix}__body`}>{children}</div>;
}

function WpPopover({
  trigger = DEFAULT_TRIGGER,
  placement = DEFAULT_PLACEMENT,
  size = DEFAULT_SIZE,
  children,
  target: targetFromProps,
  indicatesTarget: indicatesTargetFromProps,
  openOnMount = false,
  closeOnBackdrop = false,
  hideDelay = 200,
  onShow,
}: Props) {
  const [isOpenOnMount, setOpenOnMount] = useState(openOnMount);
  const [isShowPopover, setIsShowPopover] = useState(false);
  const [isOverPopover, setIsOverPopover] = useState(false);
  const [left, setLeft] = useState(null);
  const [top, setTop] = useState(null);
  const [target, setTarget] = useState(null);
  const [indicatesTarget, setIndicatesTarget] = useState(null);

  const showPopover = useCallback(() => {
    const {
      left: targetLeft,
      top: targetTop,
      height: targetHeight,
      width: targetWidth,
    } = target?.getBoundingClientRect() || {};
    const { left: indicatesTargetLeft, width: indicatesTargetWidth } = indicatesTarget?.getBoundingClientRect() || {};
    setIsShowPopover(true);
    setLeft(getLeftPosition({ placement, targetLeft, targetWidth, indicatesTargetLeft, indicatesTargetWidth }));
    setTop(getTopPosition({ placement, targetTop, targetHeight }));
    if (onShow) onShow();
  }, [target, placement, indicatesTarget, onShow]);

  const hidePopover = useCallback(() => {
    if (hideDelay > 0) setTimeout(() => setIsShowPopover(false), hideDelay);
    else setIsShowPopover(false);
  }, []);

  const handleTargetClick = useCallback(() => {
    if (isShowPopover) {
      setIsShowPopover(false);
    } else {
      showPopover();
    }
  }, [isShowPopover, showPopover]);

  const handleTargetMouseEnter = useCallback(() => showPopover(), [showPopover]);

  const handleTargetMouseLeave = useCallback(() => hidePopover(), [hidePopover]);

  const handlePopoverMouseEnter = useCallback(() => setIsOverPopover(true), []);
  const handlePopoverMouseLeave = useCallback(() => {
    if (hideDelay > 0) setTimeout(() => setIsOverPopover(false), hideDelay);
    else setIsOverPopover(false);
  }, []);

  useEffect(() => {
    if (target && isOpenOnMount) {
      showPopover();
      setOpenOnMount(false);
    }

    if (target && closeOnBackdrop) {
      target.style.zIndex = '1011';
      target.style.position = 'relative';
    }
  }, [target]);

  useEffect(() => {
    setTarget(getTargetNode(targetFromProps));
  }, [targetFromProps]);

  useEffect(() => {
    setIndicatesTarget(getTargetNode(indicatesTargetFromProps));
  }, [indicatesTargetFromProps]);

  useEffect(() => {
    if (target) {
      if (trigger === 'hover') {
        target.addEventListener('mousedown', handleTargetMouseLeave);
        target.addEventListener('mouseenter', handleTargetMouseEnter);
        target.addEventListener('mouseleave', handleTargetMouseLeave);
      } else {
        target.addEventListener('click', handleTargetClick);
        target.addEventListener('blur', hidePopover);
      }
    }

    return () => {
      if (target) {
        target.removeEventListener('mousedown', handleTargetMouseLeave);
        target.removeEventListener('mouseenter', handleTargetMouseEnter);
        target.removeEventListener('mouseleave', handleTargetMouseLeave);
        target.removeEventListener('click', handleTargetClick);
        target.removeEventListener('blur', hidePopover);
      }
    };
  }, [
    target,
    trigger,
    placement,
    handleTargetMouseEnter,
    handleTargetMouseLeave,
    handleTargetClick,
    showPopover,
    hidePopover,
  ]);

  const classes = classnames(
    classNamePrefix,
    `${classNamePrefix}--${size}`,
    `${classNamePrefix}--placement-${placement}`
  );

  if (!isOverPopover && !isShowPopover) return null;

  return (
    <Portal idPrefix="popover">
      {closeOnBackdrop && (
        <div
          className={`${classNamePrefix}__backdrop`}
          onClick={() => setIsShowPopover(false)}
          onKeyDown={() => setIsShowPopover(false)}
        />
      )}
      <div
        style={{ left, top }}
        className={classes}
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
      >
        <div className={`${classNamePrefix}__inner`}>{children}</div>
        <span className={`${classNamePrefix}__arrow`} />
      </div>
    </Portal>
  );
}

export default WpPopover;
